import { useTranslation } from "react-i18next";
import { useFetchWrapper } from "../hooks/useFetchWrapper";
import { notifySuccess, notifyError, formatAPIErrorLog, formatAPIResponseLog, logger } from "../utils/helpers";
import { useLoading } from "../utils/LoadingContext";
import { INVALID_COURSE_CODE, INVALID_COURSE_NAME } from "../utils/constants";
import { useLocalStorageNew } from "../hooks/useLocalStorageNew";
import {useConfig} from "../hooks/useConfig";
export const CourseApiCalls = () => {
    const { t } = useTranslation();
    const fetchWrapper = useFetchWrapper();
    const config = useConfig();
    const {setLoading} = useLoading();
    const [, getSession] = useLocalStorageNew("session", {});
    const[, getHostResource] = useLocalStorageNew(window.location.hostname, {});

    return {
        saveCourse,
        updateCourse,
        deleteCourse,
        getCourseByUser,
        getCourseByStudent,
        getCourseById,
        saveCourseMembers,
        checkMemberAccess,
        checkCourseCodeExist, 
    }

    // Function to Save a course  
    // returns "success: true" and the data of the course
    function saveCourse(courseBody) {
        setLoading(true);
        return fetchWrapper.post(
            {
                url: `${config.api.course}/course/save`,
                body: courseBody
            }
        ).then((result) => {
            console.log(result);
            if (result.success) {
                notifySuccess(t('common.saveSuccess', { entity: t('entities.course') }));
            } else {
                notifyError(result.message);
            }
            return result;
        }, (error) => {
            console.log(error);
            if (error?.errorMessage === INVALID_COURSE_CODE) {
                notifyError(t('course.invalidNameError'));
            } else if (error?.errorMessage === INVALID_COURSE_NAME) {
                notifyError(t('course.invalidCodeError'));
            } else {
                notifyError(t('common.saveError', { entity: t('entities.course') }));
            }
        }).finally(() => {
            setLoading(false);
        });
    }

    function updateCourse(courseBody, courseId) {
        return fetchWrapper.put(
            {
                url: `${config.api.course}/course/${courseId}`,
                body: courseBody
            }
        ).then((result) => {
            console.log(result);
            if (result.success) {
                notifySuccess(t('common.updateSuccess', { entity: t('entities.course') }));
            } else {
                notifyError(result.message);
            }
            return result;
        }, (error) => {
                notifyError(t('common.updateError', { entity: t('entities.course') }));
                console.log(error);
            });
    }

    

    // Function to Delete a course  
    // returns "success: true" and the data of the course
    function deleteCourse(courseId) {
        return fetchWrapper.delete(
            {
                url: `${config.api.course}/course/${courseId}`,
            }
        ).then((result) => {
            console.log(result);
            if (result.success) {
                notifySuccess(t('common.deleteSuccess', { entity: t('entities.course') }));
            } else {
                notifyError(result.message);
            }
            return result;
        }, (error) => {
                notifyError(t('common.deleteError', { entity: t('entities.course') }));
                console.log(error);
            });
    }

    // Function to get all the courses a user is in
    // returns array of course data 
    function getCourseByUser(idpType, intType) {
        let getUrl;
        if(idpType && intType) {
            getUrl = "/course/courses/user/all/" + idpType + "/" + intType;
        } else {
            getUrl = "/course/courses/user"
        }

        return fetchWrapper.get(
            {
                url: `${config.api.course}` + getUrl,
            }
        ).then((result) => {
            console.log(result);
            if (!result) {
                return [];
            }
            return result.result;
        }, (error) => {
                notifyError(t('common.loadError', { entity: t('entities.course') }));
                console.log(error);
                return [];
            })
    }


    // Function to get all the courses a user is in
    // returns array of course data 
    function getCourseByStudent(silent, idpType, intType) {
        if (!silent) setLoading(true);
        let getUrl;
        if (idpType && intType) {
            getUrl = "/course/student/" + idpType + "/" + intType;
        } else {
            getUrl = "/course/student/idp/int"; // passing static string so that it does not call any other API with similar path
        }
        return fetchWrapper.get(
            {
                url: `${config.api.course}` + getUrl,
            }
        ).then((result) => {
            console.log(result);
            return result.result;
        }, (error) => {
                notifyError(t('common.loadError', { entity: t('entities.course') }));
                console.log(error);
            }).finally(() => {
                if (!silent) setLoading(false);
            });
    }


    // Function to get course details
    // returns   the data of the course
    function getCourseById(courseId) {
        const session = getSession();
        let getURL = config.api.course + "/course/" + courseId;
        if(session.idpType && session.intType) {
            getURL = getURL + "/" + session.idpType + "/" + session.intType;
        } else {
            getURL = getURL + "/idp/int";
        }
        return fetchWrapper.get(
            {
                url: getURL,
            }
        ).then((result) => {
            console.log(result);
            if (!result.success) {
                notifyError(result.message);
            }
            return result;
        }, (error) => {
                notifyError(t('common.retrieveError', { entity: t('entities.course') }));
                console.log(error);
            });
    }

    function checkCourseCodeExist(courseCode) {
        setLoading(true);

        let institutionId = getHostResource().institutionId;

        if(window.location.hostname.includes('localhost')) {
            institutionId = "b6f0e9d1-1844-4c7c-9b7c-9588e3cb6615";
        }

        return fetchWrapper.get({
                url: `${config.api.course}/course/${institutionId}/existed?courseCode=${courseCode}`,
        }).then(res => {
            if (res.success) {
                return res.result;
            } else {
                notifyError(res.errorMessage);
                return true;
            }
        }).catch(err => {
            notifyError(t('common.serverError'));
            return true;
        }).finally(() => {
            setLoading(false);
        });
    }


    function saveCourseMembers(courseId, memberIdArr) {
        setLoading(true);
        return fetchWrapper.put({
                url: `${config.api.course}/course/${courseId}/members`,
                body: {members: memberIdArr}
        }).then(() => {
            notifySuccess(t('common.updateSuccess', { entity: t('entities.courseMembers') }));
            return true;
        }).catch((error) => {
            notifyError(t('common.saveError', { entity: t('entities.courseMembers') }));
            console.log(error);
            return false;
        }).finally(() => {
            setLoading(false);
        });
    }

    function checkMemberAccess(courseId) {
        return fetchWrapper.get({
            url: `${config.api.course}/course/access/${courseId}`,
            body: {},
            silent: true
        }).then((result) => {
            logger.courseId = (courseId.length === 36 ? "C#" + courseId : logger.courseId);
            formatAPIResponseLog("checkMemberAccess", result);
            return result;
        }).catch((error) => {
            console.log(error);
            formatAPIErrorLog("checkMemberAccess", error);
            return false;
        });
    }
}